import { useEffect, useRef, useState } from "react";
import {
  SessionResult,
  type SessionResult as SessionResultT,
} from "@heraion/shared";

import type { ProgressApiFetch } from "../Progress/types";

// THE hook for one computed SessionResult (GET /api/session-results/:id) —
// collapses the mobile + web-rnw copies that had drifted (CN-7 / L1): the
// mobile copy trusted the raw payload, web-rnw safeParse'd it. One endpoint,
// one behaviour: the payload is ALWAYS validated here, so a malformed row
// can never flow into totals/summary rendering on any surface.
//
// The server computes-then-stores on cache miss (the row IS the cache), so
// the first call after a save can take a beat; subsequent calls hit the
// stored row. Platform concerns are injected, same seam recipe as the
// plural sibling (Progress/useSessionResults): apiFetch from the host,
// optional per-id SWR paint (web-rnw localStorage; mobile omits today).

export type SessionResultStatus = "idle" | "loading" | "loaded" | "error";

export type SessionResultState = {
  result: SessionResultT | null;
  status: SessionResultStatus;
  /** 'parse_error' | 'api_error_<httpStatus>' — only when nothing painted. */
  error: string | null;
};

export type UseSessionResultOptions = {
  apiFetch: ProgressApiFetch;
  sessionId: string | null;
  /** The session's updatedAt, when the host has it. A correction keeps the
   *  id (#38), so a new value refetches; what is on screen stays meanwhile. */
  version?: string;
  /** Optional cached paint before the network confirms (per-id SWR). */
  readCached?: (sessionId: string) => Promise<SessionResultT | null>;
  /** Optional best-effort persist of the validated result. */
  writeCached?: (sessionId: string, result: SessionResultT) => void;
};

export function useSessionResult(
  opts: UseSessionResultOptions,
): SessionResultState {
  const { sessionId, version } = opts;
  const [result, setResult] = useState<SessionResultT | null>(null);
  const [status, setStatus] = useState<SessionResultStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  // Refs so hosts passing fresh function literals per render can't
  // re-trigger the effect (the unstable-object-dep lesson).
  const apiRef = useRef(opts.apiFetch);
  const readRef = useRef(opts.readCached);
  const writeRef = useRef(opts.writeCached);
  const shownIdRef = useRef<string | null>(null);
  // What is on screen now, read by the effect without being one of its deps.
  const resultRef = useRef<SessionResultT | null>(null);
  resultRef.current = result;
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    readRef.current = opts.readCached;
    writeRef.current = opts.writeCached;
  });

  useEffect(() => {
    const sameSession = shownIdRef.current === sessionId;
    shownIdRef.current = sessionId;
    if (sessionId === null) {
      setResult(null);
      setStatus("idle");
      setError(null);
      return;
    }
    let cancelled = false;
    // Clear the prior session's data so a detail pane never shows session
    // A's summary under a freshly-selected row B; the cached paint below
    // repaints B almost immediately when it's warm. A new version of the
    // same session keeps it on screen until the fresh one lands.
    if (!sameSession) {
      setResult(null);
      setError(null);
      setStatus("loading");
    }
    void (async () => {
      // Cache first, network second — strictly sequential, so a slow disk
      // read can never clobber an already-painted fresh network result.
      // A refetch of the same session keeps what is shown only when there is
      // something shown: a version bump before the first load must still
      // end in `loaded` or `error`, never stay `loading`.
      let painted = sameSession && resultRef.current !== null;
      const read = readRef.current;
      if (read) {
        const cached = await read(sessionId);
        if (cancelled) return;
        if (cached !== null) {
          setResult(cached);
          setStatus("loaded");
          painted = true;
        }
      }
      try {
        const raw = await apiRef.current<unknown>(
          `/api/session-results/${encodeURIComponent(sessionId)}`,
        );
        if (cancelled) return;
        const parsed = SessionResult.safeParse(raw);
        if (parsed.success) {
          setResult(parsed.data);
          setStatus("loaded");
          writeRef.current?.(sessionId, parsed.data);
        } else if (!painted) {
          // Malformed payload with nothing on screen → explicit error;
          // with a painted cached result we keep the stale (valid) data.
          setError("parse_error");
          setStatus("error");
        }
      } catch (err) {
        if (cancelled || painted) return; // keep stale on network error
        setError(
          `api_error_${(err as { status?: number }).status ?? 0}`,
        );
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, version]);

  return { result, status, error };
}
